/**
 * @fileoverview DashboardListModal Component
 *
 * Reusable list modal for the admin dashboard. Renders a scrollable list
 * of clickable items inside a centered overlay modal. Used for:
 * - Alert detail lists (overdue tasks, missing selections, etc.)
 * - Project-by-type lists
 * - All upcoming deadlines
 *
 * The parent controls what gets rendered per item via the `renderItem` prop,
 * keeping modal chrome (overlay, header, close, empty state) consistent.
 *
 * @component
 */

import React from "react";
import { FaTimes } from "react-icons/fa";
import "../../pages/admin/AdminDashboard.css";

/**
 * DashboardListModal — generic list modal for dashboard popups
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Handler to close the modal
 * @param {string} props.title - Modal header title
 * @param {string} props.subtitle - Modal header subtitle
 * @param {Array} props.items - Array of data items to render
 * @param {Function} props.renderItem - Render function: (item, index) => JSX
 * @param {string} [props.emptyMessage='No items to display'] - Default message shown when items is empty
 * @returns {JSX.Element|null} Modal overlay or null when closed
 */
const DashboardListModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  items = [],
  renderItem,
  emptyMessage = "No items to display",
}) => {
  if (!isOpen) return null;

  return (
    <div className="admin-dash-modal-overlay" onClick={onClose}>
      <div className="admin-dash-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-dash-modal-header">
          <div className="admin-dash-modal-header-title">
            <h3>{title}</h3>
          </div>
          <button
            className="admin-dash-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>
        <div className="admin-dash-modal-content">
          {items.length === 0 ? (
            <p className="admin-dash-modal-empty">{emptyMessage}</p>
          ) : (
            <>
              <h5>{subtitle}</h5>
              {items.map((item, index) => renderItem(item, index))}
            </>
          )}
        </div>
        <div className="admin-dash-modal-footer">
          <button className="admin-dash-modal-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardListModal;
